import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'react-bootstrap';
import { apiErrorText } from 'Utils/errorText';
import { READ_ONLY_TITLE } from 'Auth/permissions';
import {
  getGraphNode, updateDataGraph, tableLabel, graphHasHopLimit, DEFAULT_MAX_HOPS, graphUpdateBody, withRowFilter,
  OUTCOMES, objectsProblem, withObject, applyFix, getDraftReport, getRuleSuggestions,
} from './dataGraphApi';
import ObjectListEditor from './ObjectListEditor';
import useGraphDraft from './useGraphDraft';
import { KindFilterRow, withoutHiddenKinds } from './objectKinds';
import { NeededPanel, FindingsList, reportNoun } from './ObjectReport';

/**
 * Where a source keeps the key reads schema.table / column for a database, database.collection /
 * field path for MongoDB, Object / field API name for Salesforce. Same value, different home: the
 * placeholder says which one the consultant is in.
 */
export const keyTablePlaceholder = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'Contact';
  return e === 'mongodb' ? 'portal.members' : 'public.person';
};
export const keyColumnPlaceholder = (engine) => {
  const e = String(engine || '').toLowerCase();
  if (e === 'salesforce') return 'Person_Source_Value__c (field API name)';
  return e === 'mongodb' ? 'person_source_value (field path)' : 'person_source_value';
};

/** The draft check waits this long after the last edit before asking the platform. */
export const DRAFT_DEBOUNCE_MS = 500;

const lower = (s) => String(s || '').toLowerCase();

/** Why the extra starts cannot be saved yet, or null (the platform's own 400s, said before the save). */
export const startsProblem = (starts, keyTable, keyColumn) => {
  const list = starts || [];
  if (list.length === 0) return null;
  if (!String(keyTable || '').trim() || !String(keyColumn || '').trim()) return 'Extra starts need a key table and column: they match the selected ids.';
  const seen = new Set();
  for (let i = 0; i < list.length; i += 1) {
    const t = String(list[i].table || '').trim();
    const c = String(list[i].column || '').trim();
    if (!t || !c) return 'Every extra start needs an object and a column.';
    if (lower(t) === lower(keyTable) && lower(c) === lower(keyColumn)) return `${t}.${c} is the key itself, not an extra start.`;
    const k = `${lower(t)}.${lower(c)}`;
    if (seen.has(k)) return `${t}.${c} is an extra start twice.`;
    seen.add(k);
  }
  return null;
};

// One extra start: object (the same picker as the key table) + column.
const StartRow = ({ start, tables, columnsOf, disabled, onChange, onRemove, testId }) => {
  const [search, setSearch] = useState('');
  const [hidden, setHidden] = useState(() => new Set());
  const [columns, setColumns] = useState([]);
  useEffect(() => {
    let alive = true;
    setColumns([]);
    if (!start.table) return undefined;
    Promise.resolve(columnsOf(start.table)).then((c) => { if (alive) setColumns(c || []); }).catch(() => {});
    return () => { alive = false; };
  }, [start.table, columnsOf]);
  const query = lower(search.trim());
  const choices = withoutHiddenKinds(tables, hidden).map((n) => tableLabel(n)).filter((n) => !query || lower(n).includes(query));
  if (start.table && !choices.some((n) => lower(n) === lower(start.table))) choices.unshift(start.table);
  return (
    <div className='dg-starts__row' data-testid={testId}>
      <span className='dg-picker'>
        {!disabled && tables.length > 0 && (
          <input className='dg-input' value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${tables.length} objects…`}
            aria-label='Search the start object' data-testid={`${testId}-search`} />
        )}
        {tables.length > 0 ? (
          <select className='dg-input' value={start.table} onChange={(e) => onChange({ table: e.target.value, column: '' })} disabled={disabled}
            data-testid={`${testId}-table`} aria-label='Start object'>
            <option value=''>object…</option>
            {choices.map((n) => <option key={n} value={n}>{n}</option>)}
          </select>
        ) : (
          <input className='dg-input' value={start.table} onChange={(e) => onChange({ table: e.target.value, column: start.column })} disabled={disabled}
            placeholder='object' data-testid={`${testId}-table`} aria-label='Start object' />
        )}
        {!disabled && tables.length > 0 && <KindFilterRow items={tables} hidden={hidden} onChange={setHidden} listName='start object' testId={`${testId}-kinds`} />}
      </span>
      {columns.length > 0 ? (
        <select className='dg-input' value={start.column} onChange={(e) => onChange({ table: start.table, column: e.target.value })} disabled={disabled}
          data-testid={`${testId}-column`} aria-label='Start column'>
          <option value=''>column…</option>
          {start.column && !columns.includes(start.column) && <option value={start.column}>{start.column}</option>}
          {columns.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      ) : (
        <input className='dg-input' value={start.column} onChange={(e) => onChange({ table: start.table, column: e.target.value })} disabled={disabled}
          placeholder='column holding the selected ids' data-testid={`${testId}-column`} aria-label='Start column' />
      )}
      {!disabled && <Button size='sm' variant='link' className='p-0' onClick={onRemove} data-testid={`${testId}-remove`}>Remove</Button>}
    </div>
  );
};

// A data graph's own settings, above its explorer: the name subsets pick it by; the key — its table is
// where a subset's row selection starts when this source is the parent (the subset editor shows it
// read-only), and with other sources rows are matched on it (the parent's key name is the subset's
// shared key name); the extra starts (3.1: other columns the selected ids are matched in); and what the
// graph asks for (contract 3.0, 2026-11-03): EVERYTHING CONNECTED to the key table (relational: to a hop
// limit; MongoDB and Salesforce walk without one), or A LIST of objects, each reached by rules
// (ObjectListEditor; the ticks on the page's object list edit the same list). Beside the list, "Needed by
// this list" (the platform's draft isolation report, re-asked as the settings are edited): what a
// provision will do with every lookup to an object off the list. Table names come from the graph's node
// list, columns from the node API. `draft` is the page's one draft (useGraphDraft); without one the panel
// keeps its own.
const GraphConfigPanel = ({ graph, connection, tables, canEdit, onSaved, testReport, onDraftReport, draft: pageDraft }) => {
  const engine = (connection && (connection.type || connection.engine)) || (graph && graph.engine);
  const noun = reportNoun(engine);
  const hopLimited = graphHasHopLimit(connection || { datasourceType: graph && graph.datasourceType });
  const ownDraft = useGraphDraft(graph, { hopLimited, onSaved });
  const { form, set, setObjects, setStarts, dirty, busy, notice, setNotice, save, body: draftBody } = pageDraft || ownDraft;
  const [columns, setColumns] = useState([]);
  const [draft, setDraft] = useState({ report: null, error: null, loading: false });
  const [suggest, setSuggest] = useState({ items: null, loading: false, error: null });
  // The key table picker: every profiled object, narrowed by a search and the kind toggles.
  const [keySearch, setKeySearch] = useState('');
  const [keyHiddenKinds, setKeyHiddenKinds] = useState(() => new Set());
  const [filterBusy, setFilterBusy] = useState(false);

  const list = form.outcome === 'LIST';
  const dataSourceId = graph && graph.dataSourceId;

  // Columns per table, read once each from the node API (the key picker and every rule share them).
  const columnCache = useRef(new Map());
  useEffect(() => { columnCache.current = new Map(); }, [dataSourceId]);
  const columnsOf = useCallback((name) => {
    const key = lower(name);
    if (!key || dataSourceId == null) return Promise.resolve([]);
    if (!columnCache.current.has(key)) {
      const node = (tables || []).find((n) => lower(tableLabel(n)) === key);
      const dot = String(name).indexOf('.');
      const table = node ? node.table : (dot > 0 ? String(name).slice(dot + 1) : String(name));
      const schema = node ? node.schema : (dot > 0 ? String(name).slice(0, dot) : undefined);
      columnCache.current.set(key, getGraphNode(dataSourceId, table, schema)
        .then((d) => ((d && d.columns) || []).map((c) => c.name))
        .catch(() => { columnCache.current.delete(key); return []; }));
    }
    return columnCache.current.get(key);
  }, [dataSourceId, tables]);

  const keyNode = useMemo(
    () => (tables || []).find((n) => lower(tableLabel(n)) === lower(form.keyTable.trim())),
    [tables, form.keyTable],
  );
  useEffect(() => {
    if (!graph || !form.keyTable.trim()) { setColumns([]); return undefined; }
    let alive = true;
    columnsOf(form.keyTable.trim()).then((c) => { if (alive) setColumns(c || []); });
    return () => { alive = false; };
  }, [graph, form.keyTable, columnsOf]);

  const halfKey = Boolean(form.keyTable.trim()) !== Boolean(form.keyColumn.trim());
  const hops = form.maxHops.trim();
  const showHops = hopLimited && !list;
  const badHops = showHops && hops !== '' && !/^\d+$/.test(hops);
  const listProblem = list
    ? (!form.keyTable.trim() || !form.keyColumn.trim()
      ? 'A list needs a key table and column: the selection starts there.'
      : objectsProblem(form.objects, form.keyTable.trim()))
    : null;
  const startProblem = startsProblem(form.starts, form.keyTable, form.keyColumn);

  // The draft check (contract 3.0): the isolation report of what the editor holds, saved or not, from the plan
  // alone — "Needed by this list" before the Test, with the planner's derived rules for objects that have none
  // (3.1). Re-asked DRAFT_DEBOUNCE_MS after the last edit that changes the plan (name and description do not);
  // skipped while the settings could not be saved anyway. A list's ranked rule suggestions come with it, so a
  // derived rule's alternatives are a click away.
  const planKey = JSON.stringify([form.outcome, form.keyTable.trim(), form.keyColumn.trim(), showHops ? hops : '', form.objects, form.starts]);
  const canDraft = Boolean(graph) && Boolean(form.keyTable.trim()) && Boolean(form.keyColumn.trim()) && !halfKey && !badHops && !listProblem && !startProblem;
  const askSuggestions = useCallback(async () => {
    setSuggest({ items: null, loading: true, error: null });
    try {
      const items = await getRuleSuggestions({ graphId: graph.id, ...draftBody, tableFilters: graph.tableFilters || [] });
      setSuggest({ items, loading: false, error: null });
    } catch (e) {
      setSuggest({ items: null, loading: false, error: apiErrorText(e, 'Could not suggest rules') });
    }
  }, [graph, draftBody]);
  const draftSeq = useRef(0);
  useEffect(() => {
    if (!canDraft) { setDraft({ report: null, error: null, loading: false }); return undefined; }
    const seq = draftSeq.current + 1;
    draftSeq.current = seq;
    setDraft((d) => ({ ...d, loading: true }));
    const t = setTimeout(() => {
      getDraftReport({ graphId: graph.id, ...draftBody, tableFilters: graph.tableFilters || [] })
        .then((report) => { if (draftSeq.current === seq) setDraft({ report: report || null, error: null, loading: false }); })
        .catch((e) => { if (draftSeq.current === seq) setDraft({ report: null, error: apiErrorText(e, 'Could not check the list'), loading: false }); });
      if (list && form.objects.length > 0) askSuggestions();
    }, DRAFT_DEBOUNCE_MS);
    return () => clearTimeout(t);
    // planKey stands for every field of `form` the plan reads
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey, canDraft, graph]);
  useEffect(() => { if (onDraftReport) onDraftReport(draft.report); }, [draft.report, onDraftReport]);

  // A finding's fix changes the list: TICK lists the object, RULE adds (or swaps in) the rule.
  const onFix = useCallback((fix) => setObjects((objects) => applyFix(objects, fix)), [setObjects]);

  // The last Test's per-rule counts, by rule id, to show beside each rule (only while the Test is the saved graph's).
  const ruleCounts = useMemo(() => {
    const out = {};
    if (!testReport || !testReport.counted) return out;
    (testReport.objects || []).forEach((o) => (o.rules || []).forEach((r) => { if (r && r.id) out[r.id] = r; }));
    return out;
  }, [testReport]);

  const removeFilter = async (table) => {
    setFilterBusy(true);
    setNotice(null);
    try {
      const saved = await updateDataGraph(graph.id, graphUpdateBody(graph, withRowFilter(graph, table, '')));
      if (onSaved) onSaved(saved);
    } catch (e) {
      setNotice({ tone: 'error', text: apiErrorText(e, 'Could not remove the row filter') });
    } finally {
      setFilterBusy(false);
    }
  };

  const disabled = !canEdit;
  const listed = useMemo(() => new Set(form.objects.map((o) => lower(o.name))), [form.objects]);
  const tableNames = (tables || []).map((n) => tableLabel(n));
  const keyInProfile = !form.keyTable.trim() || tableNames.length === 0 || Boolean(keyNode);
  const keyQuery = lower(keySearch.trim());
  const keyChoices = withoutHiddenKinds(tables, keyHiddenKinds).map((n) => tableLabel(n))
    .filter((n) => !keyQuery || lower(n).includes(keyQuery));
  // The chosen key stays pickable whatever the search and toggles hide.
  if (keyNode && !keyChoices.some((n) => lower(n) === lower(form.keyTable.trim()))) keyChoices.unshift(tableLabel(keyNode));
  const neededPanel = (
    <div className='dg-needed-side' data-testid='dg-config-needed'>
      {draft.report && <FindingsList findings={draft.report.findings} testId='dg-config-findings' onFix={list && !disabled ? onFix : undefined} />}
      {draft.report
        ? (
          <NeededPanel needed={draft.report.needed} counted={Boolean(draft.report.counted)} listed={listed} noun={noun}
            onAdd={list && !disabled ? (name) => setObjects(withObject(form.objects, name)) : null} testId='dg-config-needed-list' />
        )
        : (
          <div className='dg-muted' data-testid='dg-config-needed-idle'>
            <div className='dg-section__title'>Needed by this list</div>
            {draft.loading ? 'Checking…' : (canDraft ? 'Nothing to show yet.' : 'Set the key table and column to see what the list needs.')}
          </div>
        )}
      {draft.error && <div className='dg-error' data-testid='dg-config-needed-error'>{draft.error}</div>}
    </div>
  );

  return (
    <div className='dg-panel mb-3' data-testid='dg-config'>
      <div className='dg-panel__head'>
        <div className='dg-panel__title'>Data graph settings</div>
        <div className='dg-muted'>What a subset pulls when it picks this data graph for {connection ? connection.name : 'this source'}.</div>
      </div>
      <div className='dg-config'>
        <label className='dg-config__field'>Name
          <input className='dg-input' value={form.name} onChange={set('name')} disabled={disabled} data-testid='dg-config-name' />
        </label>
        <label className='dg-config__field' title='Shown as the shared key of a subset whose parent uses this data graph, e.g. "Member number"'>Key name
          <input className='dg-input' value={form.keyName} onChange={set('keyName')} disabled={disabled} placeholder='Member number'
            data-testid='dg-config-key-name' />
        </label>
        <label className='dg-config__field' title={'Where a subset’s row selection starts when this source is its parent; with other sources, the table rows are matched on'}>Key table
          {tableNames.length > 0 ? (
            <span className='dg-picker'>
              {!disabled && (
                <input className='dg-input' value={keySearch} onChange={(e) => setKeySearch(e.target.value)}
                  placeholder={`Search ${tableNames.length} objects…`} aria-label='Search the key table'
                  data-testid='dg-config-key-table-search' />
              )}
              <select className='dg-input' value={form.keyTable} onChange={set('keyTable')} disabled={disabled} data-testid='dg-config-key-table'>
                <option value=''>No key</option>
                {form.keyTable && !keyNode && <option value={form.keyTable}>{form.keyTable} (not in the latest profile)</option>}
                {keyChoices.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </span>
          ) : (
            <input className='dg-input' value={form.keyTable} onChange={set('keyTable')} disabled={disabled}
              placeholder={keyTablePlaceholder(engine)} data-testid='dg-config-key-table' />
          )}
        </label>
        <label className='dg-config__field'>Key column
          {columns.length > 0 ? (
            <select className='dg-input' value={form.keyColumn} onChange={set('keyColumn')} disabled={disabled} data-testid='dg-config-key-column'>
              <option value=''>column…</option>
              {form.keyColumn && !columns.includes(form.keyColumn) && <option value={form.keyColumn}>{form.keyColumn} (not in the latest profile)</option>}
              {columns.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          ) : (
            <input className='dg-input' value={form.keyColumn} onChange={set('keyColumn')} disabled={disabled}
              placeholder={keyColumnPlaceholder(engine)} data-testid='dg-config-key-column' />
          )}
        </label>
        {showHops && (
          <label className='dg-config__field' title='How many steps from the key table the walk follows child tables; what the reached rows reference is always included'>Max child hops
            <input className='dg-input' type='number' min='0' max='64' value={form.maxHops} onChange={set('maxHops')} disabled={disabled}
              placeholder={`${DEFAULT_MAX_HOPS} (default)`} data-testid='dg-config-max-hops' />
          </label>
        )}
        {!hopLimited && !list && (
          <div className='dg-config__field' data-testid='dg-config-no-hops'>Max child hops
            <span className='dg-muted'>No limit: the walk follows the shared key and this graph&apos;s links.</span>
          </div>
        )}
      </div>
      {tableNames.length > 0 && !disabled && (
        <KindFilterRow items={tables} hidden={keyHiddenKinds} onChange={setKeyHiddenKinds} listName='Key table' testId='dg-config-key-kinds' />
      )}
      <div className='dg-starts' data-testid='dg-config-starts'>
        <div className='dg-starts__title'
          title={'Rows of another object whose column holds a selected id are start rows too — the same ids, whatever the selection (ID in, Where or All on the key table). Each becomes the rule object.column IN key table.key column; the object is listed if it is not already. An id that matches no key-table row seeds nothing.'}>
          <span style={{ textDecoration: 'underline dotted', textUnderlineOffset: 3, cursor: 'help' }}>Also match the selected ids in</span>
          {form.starts.length === 0 && <span className='dg-muted'>— no other columns</span>}
        </div>
        {form.starts.map((st, i) => (
          <StartRow key={i} start={st} tables={tables || []} columnsOf={columnsOf} disabled={disabled} testId={`dg-config-start-${i}`}
            onChange={(next) => setStarts(form.starts.map((x, j) => (j === i ? next : x)))}
            onRemove={() => setStarts(form.starts.filter((_, j) => j !== i))} />
        ))}
        {!disabled && (
          <Button size='sm' variant='link' className='p-0 mt-1' data-testid='dg-config-start-add' disabled={!form.keyTable.trim()}
            title={!form.keyTable.trim() ? 'Set the key table first: an extra start matches its selected ids' : undefined}
            onClick={() => setStarts([...form.starts, { table: '', column: '' }])}>+ Add another start</Button>
        )}
      </div>
      <label className='dg-config__field mt-2'>Description
        <input className='dg-input' value={form.description} onChange={set('description')} disabled={disabled}
          placeholder='What this data graph is for' data-testid='dg-config-description' />
      </label>
      {!keyInProfile && (
        <div className='dg-error' data-testid='dg-config-key-missing'>{form.keyTable} is not in the latest profile: re-sync the schema, or pick another key table.</div>
      )}
      {(graph.warnings || []).length > 0 && (
        <ul className='dg-warnings' data-testid='dg-config-warnings'>
          {graph.warnings.map((w, i) => <li key={i}>{typeof w === 'string' ? w : (w.message || w.code)}</li>)}
        </ul>
      )}
      <div className='dg-outcome' role='radiogroup' aria-label='What this data graph pulls' data-testid='dg-config-outcome'>
        {OUTCOMES.map(([v, label]) => (
          <label key={v}>
            <input type='radio' name={`dg-outcome-${graph.id}`} value={v} checked={form.outcome === v} disabled={disabled}
              onChange={set('outcome')} data-testid={`dg-config-outcome-${v}`} />
            {label}
          </label>
        ))}
      </div>
      <div className='dg-muted dg-note mt-1' data-testid='dg-config-outcome-help'>
        {list
          ? 'Only the objects listed below are saved: each is reached by its rules, and what the saved rows look up among them comes along for integrity.'
          : `Everything linked below the key table${showHops ? ' to the hop limit' : ''}, plus what those rows reference.`}
      </div>
      {list ? (
        <div className='dg-listedit'>
          <ObjectListEditor objects={form.objects} onChange={setObjects} keyTable={form.keyTable.trim()} tables={tables}
            columnsOf={columnsOf} disabled={disabled} counts={ruleCounts} suggestions={suggest.items}
            onSuggest={disabled ? null : askSuggestions} suggesting={suggest.loading} suggestError={suggest.error} />
          {neededPanel}
        </div>
      ) : (
        <div className='mt-2'>{neededPanel}</div>
      )}
      {(graph.tableFilters || []).length > 0 && (
        <div className='mt-2' data-testid='dg-config-filters'>
          <div className='dg-config__field'>Row filters</div>
          {graph.tableFilters.map((f) => (
            <div className='dg-filter__row' key={f.table} data-testid={`dg-config-filter-${f.table}`}>
              <span className='dg-mono'>{f.table}</span>
              <span className='dg-mono dg-muted dg-clip' title={f.expression}>{f.expression}</span>
              {canEdit && (
                <Button size='sm' variant='link' className='p-0' disabled={busy || filterBusy} data-testid={`dg-config-filter-remove-${f.table}`}
                  onClick={() => removeFilter(f.table)}>Remove</Button>
              )}
            </div>
          ))}
        </div>
      )}
      {halfKey && <div className='dg-error' data-testid='dg-config-half-key'>A key needs both a table and a column, or neither.</div>}
      {badHops && <div className='dg-error'>Max child hops is a whole number from 0 to 64.</div>}
      {listProblem && !halfKey && <div className='dg-error' data-testid='dg-config-list-problem'>{listProblem}</div>}
      {startProblem && !halfKey && <div className='dg-error' data-testid='dg-config-starts-problem'>{startProblem}</div>}
      <div className='dg-config__actions'>
        <Button size='sm' onClick={save} data-testid='dg-config-save'
          disabled={disabled || busy || !dirty || !form.name.trim() || halfKey || badHops || Boolean(listProblem) || Boolean(startProblem)}
          title={disabled ? READ_ONLY_TITLE : undefined}>
          {busy ? 'Saving…' : 'Save settings'}
        </Button>
        {dirty && !busy && <span className='dg-muted' data-testid='dg-config-unsaved'>Unsaved changes</span>}
        {notice && <span className={notice.tone === 'error' ? 'dg-error mt-0' : 'dg-muted'} data-testid='dg-config-notice'>{notice.text}</span>}
      </div>
    </div>
  );
};

export default GraphConfigPanel;
